import { sortByDescendingShortSide, getPossibleOrientations, findBestPosition, applySortStrategy, SpatialGrid } from './common.js';

/**
 * Try to optimize with a specific number of sheets
 */
export const tryOptimizeWithSheets = (allPieces, numSheets, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const layouts = [];
  const grids = [];
  const unplacedPieces = [];
  const processQueue = sortStrategy ? applySortStrategy(allPieces, sortStrategy) : sortByDescendingShortSide(allPieces);

  for (let sheetNum = 1; sheetNum <= numSheets; sheetNum++) {
    layouts.push({ id: sheetNum, width: sheetWidth, height: sheetHeight, pieces: [] });
    grids.push(new SpatialGrid(sheetWidth, sheetHeight));
  }

  for (const piece of processQueue) {
    let placed = false;
    const orientations = getPossibleOrientations(piece);

    for (const orientation of orientations) {
      if (placed) break;
      const sheetOrder = layouts
        .map((layout, index) => ({ layout, index, pieceCount: layout.pieces.length }))
        .sort((a, b) => b.pieceCount - a.pieceCount);

      for (const { layout, index } of sheetOrder) {
        const position = findBestPosition(orientation, layout.pieces, sheetWidth, sheetHeight, cutWidth, grids[index]);
        if (position) {
          const placedPiece = { ...orientation, x: position.x, y: position.y };
          layout.pieces.push(placedPiece);
          grids[index].insert(placedPiece);
          placed = true;
          break;
        }
      }
    }
    if (!placed) unplacedPieces.push(piece);
  }

  return {
    layouts: layouts.filter(layout => layout.pieces.length > 0),
    unplacedPieces,
    unplacedCount: unplacedPieces.length
  };
};

/**
 * Bottom-Left algorithm: iteratively tries increasing sheet counts
 */
export const bottomLeftOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const totalPieceArea = allPieces.reduce((sum, p) => sum + p.width * p.height, 0);
  const sheetArea = sheetWidth * sheetHeight;
  const theoreticalMin = Math.ceil(totalPieceArea / sheetArea);

  let sheetsToTry = Math.max(theoreticalMin, 1);
  const maxSheets = allPieces.length;
  let bestResult = null;
  let lastUnplacedCount = allPieces.length;

  while (sheetsToTry <= maxSheets) {
    const result = tryOptimizeWithSheets(allPieces, sheetsToTry, sheetWidth, sheetHeight, cutWidth, sortStrategy);
    if (result.unplacedCount === 0) return result;
    if (!bestResult || result.unplacedCount < bestResult.unplacedCount) bestResult = result;
    if (result.unplacedCount === lastUnplacedCount && sheetsToTry > theoreticalMin + 10) break;
    lastUnplacedCount = result.unplacedCount;
    sheetsToTry++;
  }
  return bestResult;
};
